/**
 * Inline feedback for messaging actions — used for failures that belong next to
 * the control that caused them (inside an open dialog, on a channel row, above
 * the composer). Transient success confirmations are toasts instead
 * (components/ui/use-toast → the root <Toaster />).
 *
 * A dismissable strip; success notices auto-clear, errors stay until dismissed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';

export interface Notice {
  kind: 'success' | 'error';
  title: string;
  description?: string;
  /** Optional inline action, e.g. "Open task". */
  action?: { label: string; onClick: () => void };
}

export function useInlineNotice(autoClearMs = 6000) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setNotice(null);
  }, []);

  const show = useCallback(
    (next: Notice) => {
      if (timer.current) clearTimeout(timer.current);
      setNotice(next);
      // Errors stay until dismissed; successes fade on their own.
      if (next.kind === 'success' && autoClearMs > 0) {
        timer.current = setTimeout(() => setNotice(null), autoClearMs);
      }
    },
    [autoClearMs],
  );

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { notice, show, clear };
}

interface InlineNoticeProps {
  notice: Notice | null;
  onClose: () => void;
  className?: string;
}

export function InlineNotice({ notice, onClose, className }: InlineNoticeProps) {
  const { t } = useAppTranslation();
  if (!notice) return null;
  const isError = notice.kind === 'error';
  const Icon = isError ? AlertCircle : CheckCircle2;
  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        isError
          ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
          : 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200',
        className,
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-medium">{notice.title}</div>
        {notice.description && <div className="break-words text-xs opacity-90">{notice.description}</div>}
      </div>
      {notice.action && (
        <button
          type="button"
          onClick={notice.action.onClick}
          className="flex-shrink-0 text-xs font-medium underline underline-offset-2"
        >
          {notice.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="flex-shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
        title={t('messaging.common.dismiss', 'Dismiss')}
        aria-label={t('messaging.common.dismiss', 'Dismiss')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default InlineNotice;
